import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";

export interface EarlyAccessRequestRow {
  id: string;
  name: string;
  email: string;
  business: string;
  status: string;
  note: string | null;
  approved_at: string | null;
  claimed_user_id: string | null;
  claimed_at: string | null;
  created_at: string;
}

/** Public: anyone can ask for early access while billing isn't live. */
export const requestEarlyAccess = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z
      .object({
        name: z.string().trim().min(1).max(120),
        email: z.string().trim().email().max(320),
        business: z.string().trim().min(1).max(1000),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const { callerKey, hashedKey, rateLimit } = await import("./rate-limit.server");
    const [byIp, byEmail] = await Promise.all([
      rateLimit("early_access:ip", callerKey("early"), 8, 3600),
      rateLimit("early_access:email", hashedKey(data.email), 3, 86400),
    ]);
    if (!byIp.allowed || !byEmail.allowed) {
      return { ok: false as const, message: "Too many requests. Try again a bit later." };
    }

    const email = data.email.toLowerCase();
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: existing } = await supabaseAdmin
      .from("early_access_requests")
      .select("id, status")
      .ilike("email", email)
      .maybeSingle();

    if (existing) {
      // Never overwrite an existing request: the submitter hasn't proven they own the email.
      if (existing.status === "approved") {
        return {
          ok: true as const,
          approved: true as const,
          message: "You're already approved — create your account with this email.",
        };
      }
      return {
        ok: true as const,
        approved: false as const,
        message: "You're on the list — we'll email you when your access is ready.",
      };
    }

    const { data: inserted, error } = await supabaseAdmin
      .from("early_access_requests")
      .insert({
        name: data.name,
        email,
        business: data.business,
      })
      .select("id")
      .maybeSingle();
    if (error) throw new Error(error.message);

    const { notifyAdminsOfEarlyAccessRequest } = await import("./early-access-notify.server");
    await notifyAdminsOfEarlyAccessRequest({
      requestId: inserted?.id ?? email,
      name: data.name,
      email,
      business: data.business,
    });

    return {
      ok: true as const,
      approved: false as const,
      message: "You're on the list — we'll email you when your access is ready.",
    };
  });

/**
 * Called right after sign-in: stamps the early-access cohort onto the profile
 * so approved members stay identifiable once billing goes live.
 */
export const syncEarlyAccess = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Only a confirmed mailbox may claim anything tied to an email address.
    const { data: authUser } = await supabaseAdmin.auth.admin.getUserById(context.userId);
    const user = authUser?.user;
    const email = String(user?.email ?? "").toLowerCase();
    if (!user || !email || !user.email_confirmed_at) return { ok: false as const };

    let { data: req } = await supabaseAdmin
      .from("early_access_requests")
      .select("id, status, note")
      .ilike("email", email)
      .maybeSingle();

    // Open beta: the seat is granted here, after the email is proven, never
    // from the unauthenticated sign-up form.
    const wantsBeta = Boolean((user.user_metadata as Record<string, unknown> | null)?.["beta_intent"]);
    if (wantsBeta && (!req || req.status !== "approved")) {
      const nowIso = new Date().toISOString();
      if (req) {
        if (req.status !== "declined") {
          await supabaseAdmin
            .from("early_access_requests")
            .update({ status: "approved", approved_at: nowIso, note: "beta" })
            .eq("id", req.id);
          req = { ...req, status: "approved", note: "beta" };
        }
      } else {
        const { data: inserted } = await supabaseAdmin
          .from("early_access_requests")
          .insert({
            name: "Beta tester",
            email,
            business: "Joined the open beta",
            status: "approved",
            approved_at: nowIso,
            note: "beta",
          })
          .select("id, status, note")
          .maybeSingle();
        req = inserted;
      }
    }
    if (!req || req.status !== "approved") return { ok: false as const };

    const now = new Date().toISOString();
    await supabaseAdmin
      .from("early_access_requests")
      .update({ claimed_user_id: context.userId, claimed_at: now })
      .eq("id", req.id)
      .is("claimed_user_id", null);

    const { data: profile } = await supabaseAdmin
      .from("profiles")
      .select("early_access")
      .eq("user_id", context.userId)
      .maybeSingle();

    if (!profile?.early_access) {
      await supabaseAdmin
        .from("profiles")
        .upsert(
          { user_id: context.userId, early_access: true, early_access_granted_at: now },
          { onConflict: "user_id" },
        );
    }

    // Beta seats: activate once, and never resurrect a seat an admin ended.
    if (req.note === "beta") {
      const { data: betaProfile } = await supabaseAdmin
        .from("profiles")
        .select("beta_status")
        .eq("user_id", context.userId)
        .maybeSingle();
      if (!betaProfile || betaProfile.beta_status === "none") {
        await supabaseAdmin
          .from("profiles")
          .upsert(
            {
              user_id: context.userId,
              beta_tester: true,
              beta_status: "active",
              beta_joined_at: now,
            },
            { onConflict: "user_id" },
          );
      }
    }

    return { ok: true as const };
  });

async function assertAdmin(context: { supabase: any; userId: string }) {
  const { data } = await context.supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", context.userId)
    .eq("role", "admin")
    .maybeSingle();
  if (!data) throw new Error("Not authorized");
}

export const adminEarlyAccess = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context);
    const { data, error } = await context.supabase
      .from("early_access_requests")
      .select("id, name, email, business, status, note, approved_at, claimed_user_id, claimed_at, created_at")
      .order("created_at", { ascending: false })
      .limit(500);
    if (error) throw new Error(error.message);
    return { requests: (data ?? []) as EarlyAccessRequestRow[] };
  });

export const setEarlyAccessStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        status: z.enum(["pending", "approved", "declined"]),
        note: z.string().trim().max(500).optional().nullable(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const patch = {
      status: data.status,
      approved_at: data.status === "approved" ? new Date().toISOString() : null,
      approved_by: data.status === "approved" ? context.userId : null,
      ...(data.note !== undefined ? { note: data.note ?? null } : {}),
    };

    const { data: row, error } = await supabaseAdmin
      .from("early_access_requests")
      .update(patch)
      .eq("id", data.id)
      .select("id, name, email, status, claimed_user_id")
      .maybeSingle();
    if (error) throw new Error(error.message);

    // Someone already signed up with that email? Flag them straight away.
    if (data.status === "approved" && row?.claimed_user_id) {
      await supabaseAdmin
        .from("profiles")
        .update({ early_access: true, early_access_granted_at: new Date().toISOString() })
        .eq("user_id", row.claimed_user_id)
        .eq("early_access", false);
    }

    if (data.status === "approved" && row?.email) {
      const { notifyApplicantOfApproval } = await import("./early-access-notify.server");
      await notifyApplicantOfApproval({
        requestId: row.id,
        name: row.name ?? "",
        email: row.email,
      });
    }
    return { ok: true as const };
  });


/** Public: is this email cleared to create an account? */
export const isEmailApproved = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z.object({ email: z.string().trim().email().max(320) }).parse(input),
  )
  .handler(async ({ data }) => {
    const { callerKey, rateLimit } = await import("./rate-limit.server");
    const verdict = await rateLimit("early_check:ip", callerKey("early_check"), 30, 3600);
    if (!verdict.allowed) return { approved: false as const };
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row } = await supabaseAdmin
      .from("early_access_requests")
      .select("status")
      .ilike("email", data.email.toLowerCase())
      .maybeSingle();
    return { approved: row?.status === "approved" };
  });
